<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">PATIENT DISCHARGE & BILLING</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('ipd-dashboard')">IPD</a></li>
                      <li class="breadcrumb-item active">Discharge & Billing</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="row">
              <div class="col-12 text-center py-5">
                <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;"></div>
                <p class="text-muted mt-3">Loading billing details...</p>
              </div>
            </div>

            <!-- Not Found State -->
            <div v-else-if="!admission" class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body text-center py-5">
                    <i class="mdi mdi-account-question text-warning" style="font-size: 80px;"></i>
                    <h4 class="mt-3">Admission Not Found</h4>
                    <p class="text-muted">The admission record could not be found.</p>
                    <button class="btn btn-primary" @click="goTo('ipd-dashboard')">
                      <i class="mdi mdi-arrow-left me-1"></i> Back to IPD Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content -->
            <div v-else class="row">
              <!-- Left Column: Patient Info & Actions -->
              <div class="col-lg-4">
                <!-- Patient Card -->
                <div class="card bg-primary bg-gradient text-white">
                  <div class="card-body">
                    <div class="d-flex align-items-center mb-3">
                      <div class="avatar-lg me-3">
                        <span class="avatar-title rounded-circle bg-white text-primary font-size-24">
                          {{ getInitials(admission.patientName) }}
                        </span>
                      </div>
                      <div>
                        <h5 class="text-white mb-1">{{ admission.patientName }}</h5>
                        <p class="mb-0 opacity-75">
                          {{ admission.gender }}, {{ admission.age }} yrs
                        </p>
                      </div>
                    </div>
                    <hr class="opacity-25">
                    <div class="row text-center">
                      <div class="col-4">
                        <p class="mb-1 opacity-75 small">Admission</p>
                        <h6 class="text-white mb-0 small">{{ admission.admissionNumber }}</h6>
                      </div>
                      <div class="col-4">
                        <p class="mb-1 opacity-75 small">Ward</p>
                        <h6 class="text-white mb-0 small">{{ admission.wardName }}</h6>
                      </div>
                      <div class="col-4">
                        <p class="mb-1 opacity-75 small">Days</p>
                        <h6 class="text-white mb-0">{{ admission.daysAdmitted || 1 }}</h6>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Billing Summary Card -->
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="card-title mb-0"><i class="mdi mdi-calculator me-2"></i>Billing Summary</h5>
                  </div>
                  <div class="card-body p-0">
                    <table class="table table-sm mb-0">
                      <tbody>
                      <tr>
                        <td>Total Items</td>
                        <td class="text-end fw-bold">{{ summary.totalItems }}</td>
                      </tr>
                      <tr>
                        <td>Grand Total</td>
                        <td class="text-end fw-bold text-primary">KES {{ numberFormat(summary.grandTotal) }}</td>
                      </tr>
                      <tr class="table-warning">
                        <td><i class="mdi mdi-clock-outline me-1"></i>Unbilled</td>
                        <td class="text-end fw-bold text-warning">KES {{ numberFormat(summary.unbilledTotal) }}</td>
                      </tr>
                      <tr class="table-success">
                        <td><i class="mdi mdi-check-circle me-1"></i>Billed</td>
                        <td class="text-end fw-bold text-success">KES {{ numberFormat(summary.billedTotal) }}</td>
                      </tr>
                      <tr>
                        <td>Total Paid</td>
                        <td class="text-end fw-bold text-info">KES {{ numberFormat(totalPaid) }}</td>
                      </tr>
                      <tr :class="balanceDue > 0 ? 'table-danger' : 'table-success'">
                        <td class="fw-bold">Balance Due</td>
                        <td class="text-end fw-bold">KES {{ numberFormat(balanceDue) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Generated Bills Card -->
                <div class="card" v-if="uniqueBills.length > 0">
                  <div class="card-header bg-success bg-gradient text-white py-2">
                    <h6 class="card-title mb-0"><i class="mdi mdi-receipt me-2"></i>Generated Bills ({{ uniqueBills.length }})</h6>
                  </div>
                  <div class="card-body p-2">
                    <div v-for="(bill, index) in uniqueBills" :key="bill.billNo"
                         class="d-flex justify-content-between align-items-center p-2 rounded mb-1"
                         :class="index % 2 === 0 ? 'bg-light' : ''">
                      <div>
                        <span class="fw-bold text-primary">{{ bill.billNo }}</span>
                        <br>
                        <small class="text-muted">{{ bill.itemCount }} item{{ bill.itemCount > 1 ? 's' : '' }} • KES {{ numberFormat(bill.total) }}</small>
                      </div>
                      <button class="btn btn-sm btn-outline-primary" @click="viewBill(bill.billNo)" title="View/Print Bill">
                        <i class="mdi mdi-printer"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Discharge Form -->
                <div class="card">
                  <div class="card-header bg-warning bg-gradient">
                    <h5 class="card-title text-dark mb-0"><i class="mdi mdi-clipboard-check me-2"></i>Discharge Details</h5>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Discharge Type <span class="text-danger">*</span></label>
                      <select class="form-select form-select-sm" v-model="dischargeForm.dischargeType">
                        <option value="">-- Select --</option>
                        <option value="Normal Discharge">Normal Discharge</option>
                        <option value="Discharged Against Medical Advice">DAMA</option>
                        <option value="Transferred">Transferred</option>
                        <option value="Absconded">Absconded</option>
                        <option value="Died">Died</option>
                        <option value="Referred">Referred</option>
                      </select>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Discharge Summary <span class="text-danger">*</span></label>
                      <textarea class="form-control form-control-sm" rows="2" v-model="dischargeForm.dischargeSummary" placeholder="Summary..."></textarea>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Follow-up Date</label>
                      <input type="date" class="form-control form-control-sm" v-model="dischargeForm.followUpDate">
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="card">
                  <div class="card-body">
                    <button class="btn btn-success w-100 mb-2" @click="generateInvoice" :disabled="selectedCharges.length === 0 || generatingBill">
                      <span v-if="generatingBill"><i class="mdi mdi-loading mdi-spin me-1"></i> Processing...</span>
                      <span v-else><i class="mdi mdi-receipt me-1"></i> Generate Invoice ({{ selectedCharges.length }} items)</span>
                    </button>
                    <button class="btn btn-warning w-100" @click="processDischarge" :disabled="!canDischarge || discharging">
                      <span v-if="discharging"><i class="mdi mdi-loading mdi-spin me-1"></i> Discharging...</span>
                      <span v-else><i class="mdi mdi-exit-run me-1"></i> Complete Discharge</span>
                    </button>
                    <div v-if="!canDischarge" class="alert alert-warning mt-2 mb-0 small py-2">
                      <i class="mdi mdi-alert me-1"></i>Fill discharge type & summary
                    </div>
                  </div>
                </div>

                <div class="text-center">
                  <button class="btn btn-light btn-sm" @click="goTo('ipd-dashboard')">
                    <i class="mdi mdi-arrow-left me-1"></i> Back to IPD
                  </button>
                </div>
              </div>

              <!-- Right Column: Charges -->
              <div class="col-lg-8">
                <!-- Tabs -->
                <ul class="nav nav-tabs nav-tabs-custom">
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: activeTab === 'charges' }" href="javascript:void(0);" @click="activeTab = 'charges'">
                      <i class="mdi mdi-format-list-bulleted me-1"></i> All Charges
                      <span class="badge bg-primary ms-1">{{ summary.totalItems }}</span>
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: activeTab === 'unbilled' }" href="javascript:void(0);" @click="activeTab = 'unbilled'; loadUnbilledItems()">
                      <i class="mdi mdi-clock-outline me-1"></i> Pending Items
                      <span class="badge bg-warning ms-1">{{ unbilledItemsCount }}</span>
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: activeTab === 'add' }" href="javascript:void(0);" @click="activeTab = 'add'">
                      <i class="mdi mdi-plus-circle me-1"></i> Add Charge
                    </a>
                  </li>
                </ul>

                <!-- Tab Content -->
                <div class="tab-content bg-white border border-top-0 rounded-bottom p-3">

                  <!-- All Charges Tab -->
                  <div v-if="activeTab === 'charges'">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <button class="btn btn-sm btn-outline-primary me-2" @click="selectAllUnbilled">
                          <i class="mdi mdi-checkbox-multiple-marked"></i> Select All Unbilled
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="clearSelection">
                          <i class="mdi mdi-checkbox-multiple-blank-outline"></i> Clear
                        </button>
                      </div>
                      <button class="btn btn-sm btn-outline-info" @click="refreshBillingSummary">
                        <i class="mdi mdi-refresh"></i> Refresh
                      </button>
                    </div>

                    <!-- Charges by Category -->
                    <div v-for="(catData, catName) in categories" :key="catName" class="mb-3" v-show="catData.items.length > 0">
                      <div class="d-flex justify-content-between align-items-center bg-light p-2 rounded">
                        <div class="d-flex align-items-center">
                          <i class="mdi me-2" :class="getCategoryIcon(catName)"></i>
                          <strong>{{ catName }}</strong>
                          <span class="badge bg-secondary ms-2">{{ catData.items.length }}</span>
                        </div>
                        <div>
                          <span class="text-warning me-2" v-if="catData.unbilled > 0">Unbilled: KES {{ numberFormat(catData.unbilled) }}</span>
                          <strong>KES {{ numberFormat(catData.total) }}</strong>
                        </div>
                      </div>
                      <table class="table table-sm table-hover mb-0">
                        <tbody>
                        <tr v-for="charge in catData.items" :key="charge.visitChargeID" :class="{ 'table-success': charge.billed === 'Yes' }">
                          <td width="40">
                            <input type="checkbox" class="form-check-input"
                                   v-model="selectedCharges"
                                   :value="charge.visitChargeID"
                                   :disabled="charge.billed === 'Yes'">
                          </td>
                          <td>
                            <span>{{ charge.serviceName }}</span>
                            <br><small class="text-muted">{{ formatDate(charge.dateCreated) }}</small>
                          </td>
                          <td class="text-center">× {{ charge.quantity }}</td>
                          <td class="text-end">{{ numberFormat(charge.unitPrice) }}</td>
                          <td class="text-end fw-bold">{{ numberFormat(charge.netAmount) }}</td>
                          <td class="text-center">
                              <span v-if="charge.billed === 'Yes'" class="badge bg-success">
                                <i class="mdi mdi-check"></i> Billed
                              </span>
                            <span v-else class="badge bg-warning">Pending</span>
                          </td>
                          <td class="text-center" style="min-width: 120px;">
                            <!-- Show bill number with print link for billed items -->
                            <a v-if="charge.billed === 'Yes' && charge.billNo"
                               href="javascript:void(0);"
                               @click="viewBill(charge.billNo)"
                               class="text-primary small text-decoration-none"
                               :title="'Print Bill: ' + charge.billNo">
                              <i class="mdi mdi-printer me-1"></i><span class="text-truncate">{{ charge.billNo }}</span>
                            </a>
                            <span v-else-if="charge.billed === 'Yes'" class="text-muted small">-</span>
                            <!-- Cancel button for unbilled items -->
                            <button v-else class="btn btn-sm btn-link text-danger p-0" @click="cancelCharge(charge)" title="Cancel">
                              <i class="mdi mdi-close"></i>
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <div v-if="summary.totalItems === 0" class="text-center py-4 text-muted">
                      <i class="mdi mdi-receipt-outline font-size-48"></i>
                      <p>No charges recorded yet</p>
                    </div>
                  </div>

                  <!-- Unbilled Source Items Tab -->
                  <div v-if="activeTab === 'unbilled'">
                    <div class="alert alert-info mb-3">
                      <i class="mdi mdi-information me-2"></i>
                      These items have been used but not yet added to the bill. Select items to add.
                    </div>

                    <div v-if="loadingUnbilled" class="text-center py-4">
                      <div class="spinner-border text-primary"></div>
                    </div>

                    <div v-else>
                      <!-- Lab Tests -->
                      <div v-if="unbilledItems.lab && unbilledItems.lab.length > 0" class="mb-3">
                        <h6 class="bg-light p-2 rounded"><i class="mdi mdi-flask me-2 text-info"></i>Lab Tests ({{ unbilledItems.lab.length }})</h6>
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in unbilledItems.lab" :key="item.requestID">
                            <td><input type="checkbox" class="form-check-input" v-model="selectedUnbilledItems" :value="{ type: 'LAB', item: item }"></td>
                            <td>{{ item.testName }}</td>
                            <td class="text-end">KES {{ numberFormat(item.price || item.amount) }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Pharmacy -->
                      <div v-if="unbilledItems.pharmacy && unbilledItems.pharmacy.length > 0" class="mb-3">
                        <h6 class="bg-light p-2 rounded"><i class="mdi mdi-pill me-2 text-success"></i>Pharmacy ({{ unbilledItems.pharmacy.length }})</h6>
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in unbilledItems.pharmacy" :key="item.dispensingID">
                            <td><input type="checkbox" class="form-check-input" v-model="selectedUnbilledItems" :value="{ type: 'PHARMACY', item: item }"></td>
                            <td>{{ item.drugName }} × {{ item.quantityDispensed }}</td>
                            <td class="text-end">KES {{ numberFormat(item.totalPrice) }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Medications (IPD) -->
                      <div v-if="unbilledItems.medications && unbilledItems.medications.length > 0" class="mb-3">
                        <h6 class="bg-light p-2 rounded"><i class="mdi mdi-medical-bag me-2 text-primary"></i>Medications ({{ unbilledItems.medications.length }})</h6>
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in unbilledItems.medications" :key="item.medicationID">
                            <td><input type="checkbox" class="form-check-input" v-model="selectedUnbilledItems" :value="{ type: 'MEDICATION', item: item }"></td>
                            <td>{{ item.drugName }} ({{ item.dosage }})</td>
                            <td class="text-end">KES {{ numberFormat(item.price || item.totalPrice || 0) }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Procedures -->
                      <div v-if="unbilledItems.procedures && unbilledItems.procedures.length > 0" class="mb-3">
                        <h6 class="bg-light p-2 rounded"><i class="mdi mdi-hospital me-2 text-danger"></i>Procedures ({{ unbilledItems.procedures.length }})</h6>
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in unbilledItems.procedures" :key="item.procedureID">
                            <td><input type="checkbox" class="form-check-input" v-model="selectedUnbilledItems" :value="{ type: 'PROCEDURE', item: item }"></td>
                            <td>{{ item.procedureName }}</td>
                            <td class="text-end">KES {{ numberFormat(item.price || item.amount) }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Radiology -->
                      <div v-if="unbilledItems.radiology && unbilledItems.radiology.length > 0" class="mb-3">
                        <h6 class="bg-light p-2 rounded"><i class="mdi mdi-radiology-box me-2 text-purple"></i>Radiology ({{ unbilledItems.radiology.length }})</h6>
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in unbilledItems.radiology" :key="item.requestID">
                            <td><input type="checkbox" class="form-check-input" v-model="selectedUnbilledItems" :value="{ type: 'RADIOLOGY', item: item }"></td>
                            <td>{{ item.examName }}</td>
                            <td class="text-end">KES {{ numberFormat(item.amount) }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <div v-if="unbilledItemsCount === 0" class="text-center py-4 text-muted">
                        <i class="mdi mdi-check-circle font-size-48 text-success"></i>
                        <p>All items have been added to the bill</p>
                      </div>

                      <div v-if="selectedUnbilledItems.length > 0" class="mt-3">
                        <button class="btn btn-primary" @click="addSelectedToBill" :disabled="addingToBill">
                          <span v-if="addingToBill"><i class="mdi mdi-loading mdi-spin me-1"></i></span>
                          <span v-else><i class="mdi mdi-plus me-1"></i></span>
                          Add {{ selectedUnbilledItems.length }} Items to Bill
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Add Manual Charge Tab -->
                  <div v-if="activeTab === 'add'">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">Income Type</label>
                          <select class="form-select" v-model="selectedIncomeType" @change="onIncomeTypeChange">
                            <option value="">-- Select --</option>
                            <option v-for="item in incomeTypes" :key="item.incomeTypeId" :value="JSON.stringify(item)">
                              {{ item.incomeTypeDescription }}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label">Fee/Charge</label>
                          <select class="form-select" v-model="selectedFee" @change="onFeeChange" :disabled="!feesAndCharges.length">
                            <option value="">-- Select --</option>
                            <option v-for="item in feesAndCharges" :key="item.feeId" :value="JSON.stringify(item)">
                              {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount) }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-4">
                        <div class="mb-3">
                          <label class="form-label">Quantity</label>
                          <input type="number" class="form-control" v-model="manualCharge.quantity" min="1">
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="mb-3">
                          <label class="form-label">Unit Price</label>
                          <input type="number" class="form-control" v-model="manualCharge.unitPrice" readonly>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="mb-3">
                          <label class="form-label">Total</label>
                          <input type="text" class="form-control bg-light fw-bold" :value="'KES ' + numberFormat(manualChargeTotal)" readonly>
                        </div>
                      </div>
                    </div>
                    <button class="btn btn-primary" @click="addManualCharge" :disabled="!selectedFeeData || addingCharge">
                      <span v-if="addingCharge"><i class="mdi mdi-loading mdi-spin me-1"></i></span>
                      <i v-else class="mdi mdi-plus me-1"></i> Add Charge
                    </button>
                  </div>
                </div>

                <!-- Previous Payments -->
                <div class="card mt-3" v-if="payments.length > 0">
                  <div class="card-header">
                    <h5 class="card-title mb-0"><i class="mdi mdi-cash-check me-2 text-success"></i>Previous Payments</h5>
                  </div>
                  <div class="card-body p-0">
                    <table class="table table-sm mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Receipt</th>
                        <th>Date</th>
                        <th>Method</th>
                        <th class="text-end">Amount</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="p in payments" :key="p.receiptNo">
                        <td><code>{{ p.receiptNo }}</code></td>
                        <td>{{ formatDate(p.paymentDate) }}</td>
                        <td>{{ p.paymentMethod }}</td>
                        <td class="text-end text-success fw-bold">KES {{ numberFormat(p.amount) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Invoice Generated Modal -->
    <div class="modal fade" id="invoiceModal" tabindex="-1" v-if="admission">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title"><i class="mdi mdi-check-circle me-2"></i>Invoice Generated</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body text-center py-4">
            <i class="mdi mdi-receipt text-success" style="font-size: 60px;"></i>
            <h4 class="mt-3">{{ generatedBillNo }}</h4>
            <h2 class="text-success">KES {{ numberFormat(generatedBillAmount) }}</h2>
            <p class="text-muted">{{ selectedCharges.length }} items billed</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-primary" @click="printBill">
              <i class="mdi mdi-printer me-1"></i> Print
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Discharge Complete Modal -->
    <div class="modal fade" id="dischargeModal" tabindex="-1" v-if="admission">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title"><i class="mdi mdi-check-circle me-2"></i>Discharge Complete</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body text-center py-4">
            <i class="mdi mdi-account-check text-success" style="font-size: 60px;"></i>
            <h4 class="mt-3">Patient Discharged</h4>
            <p>{{ admission.patientName }}</p>
            <p class="text-muted mb-0">{{ admission.wardName }} | {{ admission.daysAdmitted }} days</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button type="button" class="btn btn-primary" @click="printDischargeSummary">
              <i class="mdi mdi-printer me-1"></i> Print Summary
            </button>
            <button type="button" class="btn btn-success" @click="goTo('ipd-dashboard')">
              <i class="mdi mdi-check me-1"></i> Done
            </button>
          </div>
        </div>
      </div>
    </div>

    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, biller, hospital } from "@/api";

export default {
  name: "DischargePatient",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      admissionID: '',
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      zone: '',

      loading: true,
      admission: null,
      activeTab: 'charges',

      // Charges data
      categories: {},
      summary: {
        totalItems: 0,
        billedItems: 0,
        unbilledItems: 0,
        grandTotal: 0,
        billedTotal: 0,
        unbilledTotal: 0
      },
      payments: [],
      totalPaid: 0,

      // Selection
      selectedCharges: [],

      // Unbilled items from source tables
      unbilledItems: {},
      selectedUnbilledItems: [],
      loadingUnbilled: false,
      addingToBill: false,

      // Manual charge
      incomeTypes: [],
      feesAndCharges: [],
      selectedIncomeType: '',
      selectedFee: '',
      selectedFeeData: null,
      manualCharge: { quantity: 1, unitPrice: 0 },
      addingCharge: false,

      // Discharge
      dischargeForm: {
        dischargeType: '',
        dischargeSummary: '',
        followUpDate: ''
      },

      // State
      generatingBill: false,
      discharging: false,
      generatedBillNo: '',
      generatedBillAmount: 0
    };
  },
  computed: {
    balanceDue() {
      return this.summary.grandTotal - this.totalPaid;
    },
    canDischarge() {
      return this.dischargeForm.dischargeType && this.dischargeForm.dischargeSummary;
    },
    manualChargeTotal() {
      return (this.manualCharge.quantity || 1) * (this.manualCharge.unitPrice || 0);
    },
    unbilledItemsCount() {
      let count = 0;
      Object.values(this.unbilledItems).forEach(arr => {
        if (Array.isArray(arr)) count += arr.length;
      });
      return count;
    },
    uniqueBills() {
      const billsMap = {};
      Object.values(this.categories).forEach(cat => {
        cat.items.forEach(charge => {
          if (charge.billed === 'Yes' && charge.billNo) {
            if (!billsMap[charge.billNo]) {
              billsMap[charge.billNo] = {
                billNo: charge.billNo,
                itemCount: 0,
                total: 0
              };
            }
            billsMap[charge.billNo].itemCount++;
            billsMap[charge.billNo].total += parseFloat(charge.netAmount) || 0;
          }
        });
      });
      return Object.values(billsMap);
    }
  },
  methods: {
    goTo(route) {
      this.$router.push({ name: route });
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    getCategoryIcon(cat) {
      const icons = {
        'WARD': 'mdi-bed text-primary',
        'PHARMACY': 'mdi-pill text-success',
        'LAB': 'mdi-flask text-info',
        'RADIOLOGY': 'mdi-radiology-box text-purple',
        'PROCEDURE': 'mdi-hospital text-danger',
        'CONSULTATION': 'mdi-stethoscope text-warning',
        'OTHER': 'mdi-dots-horizontal text-secondary'
      };
      return icons[cat] || icons['OTHER'];
    },

    // Load billing summary
    loadBillingSummary() {
      const data = new FormData();
      data.append("function", "getAdmissionBillingSummary");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.admission = res.data.data.admission;
              this.categories = res.data.data.categories || {};
              this.summary = res.data.data.summary || {};
              this.payments = res.data.data.payments || [];
              this.totalPaid = res.data.data.totalPaid || 0;
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    refreshBillingSummary() {
      this.loadBillingSummary();
      this.$refs.toast?.success('Refreshed');
    },

    // Load unbilled source items
    loadUnbilledItems() {
      this.loadingUnbilled = true;
      const data = new FormData();
      data.append("function", "getUnbilledSourceItems");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            this.loadingUnbilled = false;
            if (res.data.success) {
              this.unbilledItems = res.data.data || {};
            }
          })
          .catch((e) => {
            this.loadingUnbilled = false;
            console.error(e);
          });
    },

    // Selection helpers
    selectAllUnbilled() {
      this.selectedCharges = [];
      Object.values(this.categories).forEach(cat => {
        cat.items.forEach(charge => {
          if (charge.billed !== 'Yes') {
            this.selectedCharges.push(charge.visitChargeID);
          }
        });
      });
    },

    clearSelection() {
      this.selectedCharges = [];
    },

    // Add selected unbilled items to bill
    addSelectedToBill() {
      if (this.selectedUnbilledItems.length === 0) return;

      this.addingToBill = true;

      const charges = this.selectedUnbilledItems.map(sel => {
        const item = sel.item;
        let serviceName = '', price = 0, refType = '', refID = '';

        switch (sel.type) {
          case 'LAB':
            serviceName = item.testName;
            price = item.price || item.amount || 0;
            refType = 'lab_requests';
            refID = item.requestID;
            break;
          case 'PHARMACY':
            serviceName = item.drugName;
            price = item.totalPrice || 0;
            refType = 'pharmacy_dispensing';
            refID = item.dispensingID;
            break;
          case 'MEDICATION':
            serviceName = `${item.drugName} (${item.dosage})`;
            price = item.price || item.totalPrice || 0;
            refType = 'admission_medications';
            refID = item.medicationID;
            break;
          case 'PROCEDURE':
            serviceName = item.procedureName;
            price = item.price || item.amount || 0;
            refType = 'procedures';
            refID = item.procedureID;
            break;
          case 'RADIOLOGY':
            serviceName = item.examName;
            price = item.amount || 0;
            refType = 'radiology_requests';
            refID = item.requestID;
            break;
        }

        return {
          serviceCategory: sel.type === 'MEDICATION' ? 'PHARMACY' : sel.type,
          serviceName: serviceName,
          serviceCode: '',
          referenceType: refType,
          referenceID: refID,
          sourceModule: 'IPD',
          quantity: 1,
          unitPrice: price,
          billerFeeID: item.billerFeeID || '',
          billerIncomeTypeID: item.billerIncomeTypeID || '',
          billerIncomeTypeDesc: ''
        };
      });

      const data = new FormData();
      data.append("function", "addVisitChargesBulk");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("patientName", this.admission.patientName);
      data.append("charges", JSON.stringify(charges));
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            this.addingToBill = false;
            if (res.data.success) {
              this.$refs.toast?.success(`${res.data.data.insertedCount} items added to bill`);
              this.selectedUnbilledItems = [];
              this.loadBillingSummary();
              this.loadUnbilledItems();
            } else {
              this.$refs.toast?.error(res.data.message || 'Failed to add items');
            }
          })
          .catch((e) => {
            this.addingToBill = false;
            this.$refs.toast?.error('Error adding items');
          });
    },

    // Manual charge
    getIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.incomeTypes = res.data.data.incomeTypes || [];
            }
          });
    },

    onIncomeTypeChange() {
      this.feesAndCharges = [];
      this.selectedFee = '';
      this.selectedFeeData = null;

      if (this.selectedIncomeType) {
        const incomeType = JSON.parse(this.selectedIncomeType);
        this.getFeesAndCharges(incomeType.incomeTypeId);
      }
    },

    getFeesAndCharges(incomeTypeId) {
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.feesAndCharges = res.data.data.feesAndCharges || [];
            }
          });
    },

    onFeeChange() {
      if (this.selectedFee) {
        this.selectedFeeData = JSON.parse(this.selectedFee);
        this.manualCharge.unitPrice = parseFloat(this.selectedFeeData.unitFeeAmount) || 0;
      } else {
        this.selectedFeeData = null;
        this.manualCharge.unitPrice = 0;
      }
    },

    addManualCharge() {
      if (!this.selectedFeeData) return;

      this.addingCharge = true;
      const incomeType = this.selectedIncomeType ? JSON.parse(this.selectedIncomeType) : {};

      const data = new FormData();
      data.append("function", "addVisitCharge");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("patientName", this.admission.patientName);
      data.append("serviceCategory", "OTHER");
      data.append("serviceName", this.selectedFeeData.feeDescription);
      data.append("sourceModule", "MANUAL");
      data.append("quantity", this.manualCharge.quantity);
      data.append("unitPrice", this.manualCharge.unitPrice);
      data.append("billerFeeID", this.selectedFeeData.feeId);
      data.append("billerIncomeTypeID", incomeType.incomeTypeId || '');
      data.append("billerIncomeTypeDesc", incomeType.incomeTypeDescription || '');
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            this.addingCharge = false;
            if (res.data.success) {
              this.$refs.toast?.success('Charge added');
              this.selectedFee = '';
              this.selectedFeeData = null;
              this.manualCharge = { quantity: 1, unitPrice: 0 };
              this.loadBillingSummary();
            } else {
              this.$refs.toast?.error(res.data.message || 'Failed to add charge');
            }
          })
          .catch(() => {
            this.addingCharge = false;
            this.$refs.toast?.error('Error adding charge');
          });
    },

    // Cancel charge
    cancelCharge(charge) {
      if (!confirm(`Cancel charge: ${charge.serviceName}?`)) return;

      const data = new FormData();
      data.append("function", "cancelVisitCharge");
      data.append("visitChargeID", charge.visitChargeID);
      data.append("reason", "Cancelled by user");
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.$refs.toast?.success('Charge cancelled');
              this.loadBillingSummary();
            }
          });
    },

    // Generate invoice
    generateInvoice() {
      if (this.selectedCharges.length === 0) {
        this.$refs.toast?.error('Please select items to bill');
        return;
      }

      this.generatingBill = true;

      // Get selected charge details
      const selectedItems = [];
      Object.values(this.categories).forEach(cat => {
        cat.items.forEach(charge => {
          if (this.selectedCharges.includes(charge.visitChargeID)) {
            selectedItems.push(charge);
          }
        });
      });

      // Build bill items for biller API
      const billItems = selectedItems.map(charge => ({
        feeDescription: charge.serviceName,
        quantity: charge.quantity,
        unitFeeAmount: charge.unitPrice,
        amount: charge.netAmount,
        feeId: charge.billerFeeID,
        incomeTypeId: charge.billerIncomeTypeID,
        fiscalYear: new Date().getFullYear().toString(),
        customer: `Admission: ${this.admission.admissionNumber}`,
        customerName: this.admission.patientName,
        customerPhoneNumber: this.admission.phone || '',
        zone: this.zone
      }));

      // Generate bill in biller system
      const data = new FormData();
      data.append("function", "generateBill3");
      data.append("penalized", false);
      data.append("billItem", JSON.stringify(billItems));
      data.append("payNow", "true");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              const billNo = res.data.data.bill.billDetails?.billNo || '';
              this.generatedBillNo = billNo;
              this.generatedBillAmount = selectedItems.reduce((sum, c) => sum + parseFloat(c.netAmount), 0);

              // Mark charges as billed
              this.markChargesAsBilled(billNo);
            } else {
              this.generatingBill = false;
              this.$refs.toast?.error(res.data.message || 'Failed to generate bill');
            }
          })
          .catch(() => {
            this.generatingBill = false;
            this.$refs.toast?.error('Error generating bill');
          });
    },

    markChargesAsBilled(billNo) {
      const data = new FormData();
      data.append("function", "markChargesAsBilled");
      data.append("chargeIDs", JSON.stringify(this.selectedCharges));
      data.append("billNo", billNo);
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            this.generatingBill = false;
            if (res.data.success) {
              this.selectedCharges = [];
              this.loadBillingSummary();

              const modal = new bootstrap.Modal(document.getElementById('invoiceModal'));
              modal.show();
            }
          })
          .catch(() => {
            this.generatingBill = false;
          });
    },

    printBill() {
      localStorage['params'] = JSON.stringify({ billNo: this.generatedBillNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    viewBill(billNo) {
      if (!billNo) return;
      localStorage['params'] = JSON.stringify({ billNo: billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    // Process discharge
    processDischarge() {
      if (!this.canDischarge) return;

      // Check for unbilled items
      if (this.summary.unbilledItems > 0) {
        if (!confirm(`There are ${this.summary.unbilledItems} unbilled items (KES ${this.numberFormat(this.summary.unbilledTotal)}). Proceed with discharge?`)) {
          return;
        }
      }

      this.discharging = true;

      const data = new FormData();
      data.append("function", "dischargePatient");
      data.append("admissionID", this.admissionID);
      data.append("dischargeType", this.dischargeForm.dischargeType);
      data.append("dischargeSummary", this.dischargeForm.dischargeSummary);
      data.append("followUpDate", this.dischargeForm.followUpDate);
      data.append("dischargedBy", this.sessionIdNo);
      data.append("dischargedByName", this.sessionNames);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.discharging = false;
            if (res.data.success) {
              const modal = new bootstrap.Modal(document.getElementById('dischargeModal'));
              modal.show();
            } else {
              this.$refs.toast?.error(res.data.message || 'Discharge failed');
            }
          })
          .catch(() => {
            this.discharging = false;
            this.$refs.toast?.error('Error processing discharge');
          });
    },

    printDischargeSummary() {
      localStorage['params'] = JSON.stringify({ admissionID: this.admissionID });
      const routeData = this.$router.resolve({ name: 'discharge-summary', params: { id: this.admissionID } });
      window.open(routeData.href, '_blank');
    }
  },
  mounted() {
    this.admissionID = this.$route.params.id || '';
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.zone = sessionStorage.getItem("sessionZone") || '';

    if (this.admissionID) {
      this.loadBillingSummary();
      this.getIncomeTypes();
    } else {
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.avatar-lg { width: 4rem; height: 4rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 500;
}
.nav-tabs-custom .nav-link {
  border: none;
  color: #74788d;
}
.nav-tabs-custom .nav-link.active {
  color: #556ee6;
  border-bottom: 2px solid #556ee6;
  background: transparent;
}
.text-purple { color: #6f42c1; }
.table > :not(caption) > * > * { padding: 0.4rem 0.5rem; }
</style>